"use client";

/**
 * StatusToggle — pill toggle with semantic color (NOT accent-aware on purpose).
 *
 * Use cases: "🟢 active / ⚪ inactive / 🔴 error" — each carries a fixed meaning
 * tied to the color. Unlike Chip (which themes to the user's accent), these
 * stay emerald/slate/rose so users can read them at a glance.
 *
 * Optional `count` shows live numbers next to the label.
 */
export interface StatusToggleProps {
  color: "emerald" | "slate" | "rose" | "amber" | "blue";
  emoji?: string;
  label: string;
  count?: number;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}

const PALETTE: Record<StatusToggleProps["color"], { active: string; inactive: string }> = {
  emerald: {
    active: "border-emerald-500/60 bg-emerald-500/15 text-emerald-100",
    inactive: "border-slate-700/50 bg-slate-800/40 text-slate-400 hover:border-slate-600 hover:text-slate-200",
  },
  slate: {
    active: "border-slate-400/60 bg-slate-400/15 text-slate-100",
    inactive: "border-slate-700/50 bg-slate-800/40 text-slate-400 hover:border-slate-600 hover:text-slate-200",
  },
  rose: {
    active: "border-rose-500/60 bg-rose-500/15 text-rose-100",
    inactive: "border-slate-700/50 bg-slate-800/40 text-slate-400 hover:border-slate-600 hover:text-slate-200",
  },
  amber: {
    active: "border-amber-500/60 bg-amber-500/15 text-amber-100",
    inactive: "border-slate-700/50 bg-slate-800/40 text-slate-400 hover:border-slate-600 hover:text-slate-200",
  },
  blue: {
    active: "border-blue-500/60 bg-blue-500/15 text-blue-100",
    inactive: "border-slate-700/50 bg-slate-800/40 text-slate-400 hover:border-slate-600 hover:text-slate-200",
  },
};

export function StatusToggle({
  color,
  emoji,
  label,
  count,
  checked,
  onChange,
  disabled,
}: StatusToggleProps) {
  const palette = PALETTE[color];
  // GAM Brand v2: status toggles stay pills (semantic shape stays the same),
  // but adopt the active skin's spring curve for the active/inactive transition.
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{ transitionTimingFunction: "var(--gam-spring-default, cubic-bezier(0.4, 0, 0.2, 1))" }}
      className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs cursor-pointer transition disabled:opacity-40 disabled:cursor-not-allowed ${
        checked ? palette.active : palette.inactive
      }`}
    >
      {emoji && <span>{emoji}</span>}
      <span>{label}</span>
      {count !== undefined && (
        <span className="font-mono opacity-70 text-[10px]">({count.toLocaleString()})</span>
      )}
    </button>
  );
}
